import { useState } from "react";
import { Modal, Checkbox, Loading, Select } from "barte-design-system";
import { Input } from "@/components";
import { useCreateUser, useSendResetPassword } from "@/services/usersService";
import { showToast } from "@/utils";
import { useUserForm } from "../../hooks";
import { buildCreatePayload } from "../../helpers";
import { UserPermissionsInfo } from "../user-permissions-info";
import type { ModalAddUserProps } from "./modal-add-user.type";
import styles from "./modal-add-user.module.css";

const PLACEHOLDER_OPTION = { label: "Selecione um perfil", value: "", disabled: true };

export const ModalAddUser = ({ isOpen, onClose }: ModalAddUserProps) => {
  const userInfo = JSON.parse(localStorage.getItem("barte-user-info") || "{}");
  const hasBankingAccess = userInfo?.bankingStatus === "ACTIVE";

  const [hasCheckedBanking, setHasCheckedBanking] = useState(hasBankingAccess);
  const [hasCheckedAcquiring, setHasCheckedAcquiring] = useState(!hasBankingAccess);

  const {
    formState,
    setField,
    acquiringProfileOptions,
    bankingProfileOptions,
    isLoading,
  } = useUserForm({ mode: "create", isOpen });

  const { mutateAsync: createUser, isPending: isCreating } = useCreateUser();
  const { mutateAsync: sendResetPassword } = useSendResetPassword();

  const canSave =
    !!formState.email &&
    ((hasCheckedAcquiring && !!formState.acquiringProfile) ||
      (hasCheckedBanking && !!formState.bankingProfile));

  const handleToggleBanking = () => {
    const next = !hasCheckedBanking;
    setHasCheckedBanking(next);
    if (!next) setField("bankingProfile", "");
  };

  const handleToggleAcquiring = () => {
    const next = !hasCheckedAcquiring;
    setHasCheckedAcquiring(next);
    if (!next) setField("acquiringProfile", "");
  };

  const handleClose = () => {
    setHasCheckedBanking(hasBankingAccess);
    setHasCheckedAcquiring(!hasBankingAccess);
    onClose();
  };

  const handleSave = async () => {
    const payload = buildCreatePayload(
      formState.email,
      hasCheckedAcquiring ? formState.acquiringProfile : "",
      hasCheckedBanking ? formState.bankingProfile : "",
    );

    const response = await createUser({ payload });

    if (response.success) {
      await sendResetPassword({ email: formState.email });
      showToast({ type: "success", message: "Usuário criado com sucesso." });
      handleClose();
    } else {
      showToast({ type: "error", message: response.message });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Novo usuário"
      onClose={handleClose}
      footer={{
        primaryButton: {
          children: "Salvar",
          size: "sm",
          onClick: handleSave,
          disabled: !canSave,
          loading: isCreating,
        },
        secondaryButton: {
          children: "Cancelar",
          size: "sm",
          variant: "outline",
          onClick: handleClose,
        },
      }}
    >
      {isLoading ? (
        <div className={styles.loading}>
          <Loading type="page" />
        </div>
      ) : (
        <div className={styles.form}>
          <div className={styles.field}>
            <Input
              label="Email"
              name="email"
              placeholder="Email"
              value={formState.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField("email", e.target.value)}
            />
          </div>

          <div className={styles["profiles-section"]}>
            {hasBankingAccess && (
              <>
                <Checkbox
                  checked={hasCheckedBanking}
                  onChange={handleToggleBanking}
                  label="Banking"
                />
                {hasCheckedBanking && (
                  <div className={styles.field}>
                    <Select
                      label="Perfis"
                      options={[PLACEHOLDER_OPTION, ...bankingProfileOptions]}
                      value={formState.bankingProfile}
                      onChange={(val: string) => setField("bankingProfile", val)}
                      placeholder="Selecione um perfil"
                    />
                  </div>
                )}
              </>
            )}

            <Checkbox
              checked={hasCheckedAcquiring}
              onChange={handleToggleAcquiring}
              label="Adquirência"
            />
            {hasCheckedAcquiring && (
              <div className={styles.field}>
                <Select
                  label="Perfis"
                  options={[PLACEHOLDER_OPTION, ...acquiringProfileOptions]}
                  value={formState.acquiringProfile}
                  onChange={(val: string) => setField("acquiringProfile", val)}
                  placeholder="Selecione um perfil"
                />
              </div>
            )}
          </div>
        </div>
      )}
      <UserPermissionsInfo
        acquiringSelectedProfile={formState.acquiringProfile}
        bankingSelectedProfile={formState.bankingProfile}
      />
    </Modal>
  );
};
